// Student/kid → My tasks. Shows tasks a teacher gave them; tap to mark done.
// Playful styling to sit in the kid shell. docs/replan-v2.md R10.

import { useT } from '@fajrarabi/i18n/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { listMyTasks, transitionTask, type Task } from '~/lib/messaging';

export const Route = createFileRoute('/$locale/tasks')({
  component: MyTasksScreen,
});

function MyTasksScreen() {
  const t = useT();
  const qc = useQueryClient();
  const tasks = useQuery({
    queryKey: ['tasks', 'mine'],
    queryFn: ({ signal }) => listMyTasks(signal),
  });

  const transition = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Task['status'] }) =>
      transitionTask(id, status),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks', 'mine'] });
    },
  });

  return (
    <div className="mx-auto max-w-xl">
      <h1 className="mt-4 text-2xl font-bold text-burgundy-700">{t('app.tasks.my_title')}</h1>

      <div className="mt-6 space-y-3">
        {tasks.isLoading ? (
          <div className="h-24 animate-pulse rounded-[1.5rem] bg-cream-200" />
        ) : (tasks.data?.length ?? 0) === 0 ? (
          <div className="rounded-[1.5rem] bg-gradient-to-b from-sun-50 to-cream-50 p-8 text-center ring-2 ring-sun-300/40">
            <div className="text-5xl" aria-hidden="true">
              🎉
            </div>
            <p className="mt-3 font-bold text-ink-900">{t('app.tasks.my_empty')}</p>
          </div>
        ) : (
          tasks.data!.map((task) => (
            <MyTaskCard
              key={task.id}
              task={task}
              onDone={() => transition.mutate({ id: task.id, status: 'done' })}
              busy={transition.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
}

function MyTaskCard({ task, onDone, busy }: { task: Task; onDone: () => void; busy: boolean }) {
  const t = useT();
  const done = task.status === 'done' || task.status === 'verified';
  return (
    <div className="flex items-center justify-between gap-4 rounded-[1.5rem] bg-white p-5 ring-2 ring-sun-300/30">
      <div className="min-w-0">
        <p className="font-bold text-ink-900">{task.title}</p>
        {task.dueAt && (
          <p className="mt-0.5 text-sm text-ink-500">
            {t('app.tasks.due_prefix')} {task.dueAt.slice(0, 10)}
          </p>
        )}
      </div>
      {task.status === 'verified' ? (
        <span className="shrink-0 rounded-full bg-leaf/25 px-4 py-2 text-sm font-bold text-leaf">
          ⭐ {t('app.tasks.status_verified')}
        </span>
      ) : done ? (
        <span className="shrink-0 rounded-full bg-leaf/20 px-4 py-2 text-sm font-bold text-leaf">
          ✓ {t('app.tasks.status_done')}
        </span>
      ) : (
        <button
          type="button"
          onClick={onDone}
          disabled={busy}
          className="shrink-0 rounded-full bg-burgundy-500 px-6 py-3 text-sm font-bold text-cream-50 transition-colors hover:bg-burgundy-600 disabled:opacity-50"
        >
          {t('app.tasks.mark_done')}
        </button>
      )}
    </div>
  );
}
